import { useEffect, useRef, useState } from "react";
import { StyleSheet, View, TouchableOpacity, Animated, Easing, Platform } from "react-native";
import { Text, Icon } from "react-native-paper";

import { useNotification } from "@/lib/notifications";
import { fetchOnboardingStatus, triggerIdentityVerification } from "@/lib/onboarding-api";
import { colors } from "@/lib/theme";
import { useWebSocketEvent } from "@/lib/websocket";

type VerificationResult = "pending" | "approved" | "rejected" | "verification_failed";

interface VerificationStepProps {
    rnId: string;
    selfieSubmissionId: string;
    documentSubmissionId?: string;
    initialResult?: VerificationResult;
    onStartOver: () => void;
    onApproved?: () => void;
}

const FALLBACK_POLL_INTERVAL = 30_000;

export function VerificationStep({
    rnId,
    selfieSubmissionId,
    documentSubmissionId,
    initialResult,
    onStartOver,
    onApproved
}: VerificationStepProps) {
    const [result, setResult] = useState<VerificationResult>(initialResult ?? "pending");
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);
    const spinAnim = useRef(new Animated.Value(0)).current;
    const fallbackRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const { showNotification } = useNotification();

    async function checkStatus() {
        try {
            const status = await fetchOnboardingStatus(rnId);

            // Find selfie submission
            const selfieSub = status.submissions.find(
                (s) => String(s.itemId) === String(selfieSubmissionId)
            );

            // Find document submission (if provided)
            const docSub = documentSubmissionId
                ? status.submissions.find((s) => String(s.itemId) === String(documentSubmissionId))
                : null;

            if (!selfieSub) {
                return;
            }

            // Check selfie status
            if (selfieSub.status === "rejected") {
                setResult("rejected");
                setRejectionReason(selfieSub.rejectionReason);
                return;
            }
            if (selfieSub.status === "verification_failed") {
                setResult("verification_failed");
                return;
            }

            // Check document status (if tracking it)
            if (docSub) {
                if (docSub.status === "rejected") {
                    setResult("rejected");
                    setRejectionReason(
                        docSub.rejectionReason ??
                            "Your photo ID could not be verified. Please try again with a clearer image."
                    );
                    return;
                }
                if (docSub.status === "verification_failed") {
                    setResult("verification_failed");
                    return;
                }
            }

            // Both must be approved (or document not tracked)
            const selfieApproved = selfieSub.status === "approved";
            const docApproved = docSub ? docSub.status === "approved" : true;

            if (selfieApproved && docApproved) {
                setResult("approved");
                onApproved?.();
            }
        } catch {
            // Keep waiting on network errors
        }
    }

    // Primary: WebSocket push — instant notification from face-compare worker
    useWebSocketEvent("onboarding:updated", (data) => {
        if (String(data.rnId) !== rnId) {
            return;
        }
        showNotification(data.message, data.status);
        if (result === "pending") {
            checkStatus();
        }
    });

    // Fallback: slow poll (30s) in case WebSocket disconnects
    useEffect(() => {
        if (result !== "pending") {
            return;
        }
        fallbackRef.current = setInterval(checkStatus, FALLBACK_POLL_INTERVAL);
        return () => {
            if (fallbackRef.current) {
                clearInterval(fallbackRef.current);
            }
        };
    }, [rnId, selfieSubmissionId, result]);

    useEffect(() => {
        const spin = Animated.loop(
            Animated.timing(spinAnim, {
                toValue: 1,
                duration: 1500,
                easing: Easing.linear,
                useNativeDriver: Platform.OS !== "web"
            })
        );
        spin.start();
        // Do initial check if not already approved (resume case)
        if (initialResult !== "approved") {
            checkStatus();
        }

        return () => {
            spin.stop();
        };
    }, [rnId, selfieSubmissionId]);

    const spinInterp = spinAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ["0deg", "360deg"]
    });

    async function handleRetry() {
        setResult("pending");
        try {
            await triggerIdentityVerification(rnId);
        } catch {
            setResult("verification_failed");
        }
    }

    if (result === "approved") {
        return (
            <View style={styles.container}>
                <View style={styles.resultContainer}>
                    <View style={styles.successCircle}>
                        <Icon source="check" size={48} color="#FFFFFF" />
                    </View>
                    <Text style={styles.successTitle}>Identity Confirmed</Text>
                    <Text style={styles.subtitle}>
                        Your selfie matches your photo ID. You can continue with your onboarding.
                    </Text>
                </View>
            </View>
        );
    }

    if (result === "rejected") {
        return (
            <View style={styles.container}>
                <View style={styles.resultContainer}>
                    <View style={styles.failCircle}>
                        <Icon source="close" size={48} color="#FFFFFF" />
                    </View>
                    <Text style={styles.failTitle}>Verification Failed</Text>
                    <Text style={styles.subtitle}>
                        {rejectionReason ??
                            "Your selfie doesn't match your photo ID. Please try again with better lighting."}
                    </Text>
                    <TouchableOpacity style={styles.startOverButton} onPress={onStartOver}>
                        <Icon source="refresh" size={18} color="#FFFFFF" />
                        <Text style={styles.buttonText}>Start Over</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    if (result === "verification_failed") {
        return (
            <View style={styles.container}>
                <View style={styles.resultContainer}>
                    <View style={styles.warnCircle}>
                        <Icon source="alert-outline" size={48} color="#FFFFFF" />
                    </View>
                    <Text style={styles.failTitle}>Something Went Wrong</Text>
                    <Text style={styles.subtitle}>
                        We couldn't process your verification. Please try again.
                    </Text>
                    <TouchableOpacity style={styles.retryButton} onPress={handleRetry}>
                        <Icon source="refresh" size={18} color="#FFFFFF" />
                        <Text style={styles.buttonText}>Try Again</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.startOverButton} onPress={onStartOver}>
                        <Icon source="refresh" size={18} color="#FFFFFF" />
                        <Text style={styles.buttonText}>Start Over</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.resultContainer}>
                <Animated.View style={{ transform: [{ rotate: spinInterp }] }}>
                    <Icon source="loading" size={56} color={colors.primary} />
                </Animated.View>
                <Text style={styles.pendingTitle}>Verifying your identity...</Text>
                <Text style={styles.subtitle}>
                    We're comparing your selfie with your photo ID. This usually takes a few
                    seconds.
                </Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, justifyContent: "center" },
    resultContainer: { alignItems: "center", gap: 16, paddingHorizontal: 32 },
    successCircle: {
        width: 88,
        height: 88,
        borderRadius: 44,
        backgroundColor: colors.success,
        alignItems: "center",
        justifyContent: "center"
    },
    failCircle: {
        width: 88,
        height: 88,
        borderRadius: 44,
        backgroundColor: colors.error,
        alignItems: "center",
        justifyContent: "center"
    },
    warnCircle: {
        width: 88,
        height: 88,
        borderRadius: 44,
        backgroundColor: colors.warning,
        alignItems: "center",
        justifyContent: "center"
    },
    successTitle: { fontSize: 22, fontWeight: "700", color: colors.success },
    failTitle: { fontSize: 22, fontWeight: "700", color: colors.textPrimary },
    pendingTitle: { fontSize: 20, fontWeight: "700", color: colors.textPrimary },
    subtitle: { fontSize: 14, color: colors.textSecondary, textAlign: "center" },
    startOverButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingVertical: 14,
        paddingHorizontal: 28,
        marginTop: 8
    },
    retryButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: colors.warning,
        borderRadius: 10,
        paddingVertical: 14,
        paddingHorizontal: 28,
        marginTop: 8
    },
    buttonText: { fontSize: 15, fontWeight: "700", color: "#FFFFFF" }
});
